import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { UserRoundCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { solicitacoesPendentes } from '@/lib/aprovacoesApi';
import { cn } from '@/lib/utils';

const QUEM_DECIDE = ['master', 'gerente', 'diretor', 'supervisor'];

/**
 * PEDIDO DE CARTEIRA FIXADO NO TOPO (Douglas 17/09/2026).
 *
 *   "quando alguém solicitar apareça igual ao aviso da fatura que aparece do
 *    ladinho. Quando chegar um pedido de carteira, troca, aparecer e fixar ali
 *    até clicar e levar para a página de aprovação."
 *
 * A fila já existia em Painel do Gestor → Aprovações, e é lá que se decide —
 * este chip não decide nada, só não deixa o pedido morrer de silêncio. Mesmo
 * lugar e mesmo desenho dos chips do BKO: ele fica enquanto houver pendência e
 * some sozinho quando zera.
 *
 * A cor segue a régua da casa: espera é dívida, e dívida é bordô. Sem âmbar.
 */
export function AprovacoesChipInline() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const podeDecidir = QUEM_DECIDE.includes(String(profile?.role || ''));

  const { data } = useQuery({
    queryKey: ['aprovacoes', 'chip'],
    enabled: podeDecidir,
    staleTime: 60_000,
    refetchInterval: 120_000,
    queryFn: () => solicitacoesPendentes({ status: 'pendentes' }),
  });

  const totalDono = data?.length ?? 0;
  const total = totalDono;
  const maisAntigaDias = useMemo(() => {
    if (!data?.length) return 0;
    const t = Math.min(...data.map((s) => new Date(s.created_at).getTime()).filter((n) => !Number.isNaN(n)));
    return Number.isFinite(t) ? Math.floor((Date.now() - t) / 86_400_000) : 0;
  }, [data]);

  if (!podeDecidir || total === 0) return null;

  return (
    <button
      type="button"
      onClick={() => navigate('/busca-lead-360?solicitacoes=aprovacoes')}
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[11px] font-medium',
        'text-foreground/80 hover:text-foreground hover:bg-accent/60 transition-colors',
      )}
      title={`${totalDono} pedido${totalDono > 1 ? 's' : ''} de dono esperando decisão${maisAntigaDias > 0 ? ` (o mais antigo há ${maisAntigaDias} dia${maisAntigaDias > 1 ? 's' : ''})` : ''}`}
    >
      <UserRoundCheck className={cn('h-3.5 w-3.5 text-primary', maisAntigaDias >= 1 && 'animate-pulse')} />
      <span className="tabular-nums tracking-tight">{total}</span>
      <span className="hidden xl:inline text-muted-foreground">Aprovações</span>
    </button>
  );
}
